import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Constants, IntermediaryAbaErrorMap, WireAbaCodes, abaErrorMap, addDetailsApiError, transactionDetailLabels } from 'projects/money-movement-ui/src/app/shared/utilities/Constants';
import { Observable, Subject, combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ValidateInputService } from '../../../shared/services/validate-input.service';
import { abaAllowedChars, numeralsOnly } from '../../../shared/utilities/regex-patterns';
import { StandingAuthWireActions } from '../../../standing-authorizations/actions';
import { WireActions } from '../../actions';
import { WireInfo } from '../../models/wire/WireInfo';
import { AbaAsyncValidator } from './abaAsyncValidator';
import { Country } from '../../models/shared/Country';
import { selectABA, selectTaxRemittance, selectUseStandingAuth } from '../../reducers';
import { selectABAStandingAuth } from '../../../standing-authorizations/reducers'
import { FormatIsoAddressFieldsService } from '../../../shared/services/format-isoAddressFields.service';
import { selectTemplatesList } from '../../../account-info-header/reducers';
import { selectTemplateId } from '../../reducers';
import { DraftCommonWire } from '../../models/wire/WireTemplatesResponse';
import { createControl } from '../../utilities/formUtilities';

@Component({
  standalone: false,
  selector: 'mm-wire-aba',
  templateUrl: './wire-aba.component.html',
  styleUrls: ['./wire-aba.component.scss'],
  providers: [AbaAsyncValidator],
})
export class WireAbaComponent implements OnInit, OnDestroy {
  @Input() parentForm!: UntypedFormGroup;
  @Input() useSloa: boolean;
  @Input() useTemplate: boolean;
  @Input() isFromIntermediaryInterim: boolean;
  @Input() wireInfo$: Observable<WireInfo>;
  @Input() wireAbaCode$: Observable<string>;
  @Input() wireDetailsError$: Observable<string>;
  @Input() wireDetailsWarning$: Observable<string>;
  @Input() isFromCreateTransaction: boolean;
  @Input() palAccountInContext: boolean = false;
  useSloa$: Observable<boolean>;
  isISOCompliant: boolean;
  showIsoReceivingBankDetails: boolean = false;
  abaLabel: string;
  abaInfoLabel: string;
  templates: DraftCommonWire[] = [];
  templateId: string | undefined;

  controlNames = {
    aba: 'aba',
    name1: 'receivingBankName1',
    name2: 'receivingBankName2',
    address1: 'receivingBankAddress1',
    address2: 'receivingBankAddress2',
    country: 'receivingBankCountry',
    bankPhoneNumber: 'bankPhoneNumber',
    receivingBankName: 'receivingBankName',
    isoRecAddress: {
      townName: 'receivingBankCity',
      countrySubDivision: 'receivingBankState',
      country: 'country',
    }
  };
  wireInfo: WireInfo;
  serviceError: boolean;
  serviceErrorText: string;
  abaError: boolean;
  abaAllowedChars = abaAllowedChars;
  detailsApiError: string;
  detailsApiWarning: string;
  abaCodes = WireAbaCodes;
  destroy$: Subject<boolean> = new Subject<boolean>();
  useSloaAddress: string;
  countryList: Country[];
  clearingSystemId: string;
  hasSecondIdentifier: boolean;
  isoCompliantWithSecondIdentifier: boolean;

  constructor(
    private fb: UntypedFormBuilder,
    private store: Store,
    public validationService: ValidateInputService,
    private abaAsyncValidator: AbaAsyncValidator,
    private wireActions$: Actions,
    private formatIsoAddressFieldsService: FormatIsoAddressFieldsService
  ) { }

  ngOnInit(): void {
    this.isISOCompliant = this.parentForm?.value?.isISOCompliant;
    this.hasSecondIdentifier = this.parentForm?.value?.receivingBankIdentifierList?.length === 2;
    this.isoCompliantWithSecondIdentifier = this.isISOCompliant && this.hasSecondIdentifier;
    this.useSloa$ = this.store.select(selectUseStandingAuth);
    this.useSloa$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.showIsoReceivingBankDetails = true;
    });
    this.createControl(this.controlNames.aba, '', [
      Validators.required,
      Validators.minLength(Constants.abaLength),
      Validators.maxLength(Constants.abaLength),
      Validators.pattern(numeralsOnly)
    ], 
    [
      this.abaAsyncValidator.validate.bind(this.abaAsyncValidator)
    ]);

    this.createControl(this.controlNames.name1, '', [Validators.required]);
    this.createControl(this.controlNames.name2, '');
    this.createControl(this.controlNames.address1, '', [Validators.required]);
    this.createControl(this.controlNames.address2, '');
    this.createControl(this.controlNames.country, '', [Validators.required]);
    this.createControl(this.controlNames.bankPhoneNumber, '');
    this.createControl(this.controlNames.receivingBankName, '');
    this.createControl(this.controlNames.isoRecAddress.townName, '');
    this.createControl(this.controlNames.isoRecAddress.countrySubDivision, '');
    this.createControl(this.controlNames.isoRecAddress.country, '');


    this.wireInfo$
      .pipe(takeUntil(this.destroy$))
      .subscribe((wireInfo) => {
        this.isISOCompliant = this.parentForm?.value?.isISOCompliant ?? wireInfo?.isISOCompliant;
        this.countryList = wireInfo.countryList;
        if (!this.useSloa) {
          this.wireInfo = wireInfo;
          this.receivingBankName1?.setValue(
            wireInfo?.receivingBankDetails?.recBankName1
          );
          this.receivingBankName2?.setValue(
            wireInfo?.receivingBankDetails?.recBankName2
          );
          this.receivingBankAddress1?.setValue(
            wireInfo?.receivingBankDetails?.recBankAddress1
          );
          this.receivingBankAddress2?.setValue(
            wireInfo?.receivingBankDetails?.recBankAddress2
          );
          this.receivingBankCountry?.setValue(
            this.getCountryDescription(wireInfo?.countryList, wireInfo?.receivingBankDetails?.recBankCountry?.toUpperCase())
          );
          this.receivingBankPhoneNumber?.setValue(this.formatBankPhoneNumber(wireInfo));
          this.receivingBankName?.setValue(
            wireInfo?.receivingBankDetailsIso?.recBankName
          );
          this.receivingBankCity?.setValue(
            wireInfo?.receivingBankDetailsIso?.recBankAddress?.townName
          );
          this.receivingBankState?.setValue(
            wireInfo?.receivingBankDetailsIso?.recBankAddress?.countrySubDivision
          );
          this.country?.setValue(
            this.getCountryDescription(wireInfo?.countryList, wireInfo?.receivingBankDetailsIso?.recBankAddress?.country?.toUpperCase())
          );
          if(this.isISOCompliant) {
          this.useSloaAddress = this.getIntermediarySloaReadOnlyAddress(
            wireInfo?.receivingBankDetailsIso?.recBankName,
            wireInfo?.receivingBankDetailsIso?.recBankAddress?.townName,
            wireInfo?.receivingBankDetailsIso?.recBankAddress?.countrySubDivision,
            this.getCountryDescription(this.countryList, wireInfo?.receivingBankDetailsIso?.recBankAddress?.country?.toUpperCase()));
            this.clearingSystemId = this.parentForm?.value?.receivingBankClearingSysId;
          }
          else {
            this.useSloaAddress = this.getSloaReadOnlyNonCompliantAddress(wireInfo?.receivingBankDetails?.recBankName1, wireInfo?.receivingBankDetails?.recBankName2, wireInfo?.receivingBankDetails?.recBankAddress1, wireInfo?.receivingBankDetails?.recBankAddress2, wireInfo?.receivingBankDetails?.recBankCountry, wireInfo?.receivingBankDetails?.bankPhoneNum1 ?? '');
          }
        }
      });

    if (this.useSloa) {
      this.receivingBankName.setValue(
        this.parentForm?.value?.receivingBankName
      );
      this.receivingBankCity.setValue(
        this.parentForm?.value?.receivingBankCity
      );
      this.receivingBankState.setValue(
        this.parentForm?.value?.receivingBankState
      );
      this.country.setValue(
        this.parentForm?.value?.receivingBankCountry
      );
      if (this.isISOCompliant) {
        this.useSloaAddress = this.getIntermediarySloaReadOnlyAddress(this.parentForm?.value?.receivingBankName, this.parentForm?.value?.receivingBankCity, this.parentForm?.value?.receivingBankState, this.getCountryDescription(this.countryList, this.parentForm?.value?.receivingBankCountry?.toUpperCase()));
        this.clearingSystemId = this.parentForm?.value?.receivingBankClearingSysId;
      } else {
        this.useSloaAddress = this.getSloaReadOnlyNonCompliantAddress(this.parentForm?.value?.receivingBankName1, this.parentForm?.value?.receivingBankName2, this.parentForm?.value?.receivingBankAddress1, this.parentForm?.value?.receivingBankAddress2, this.parentForm?.value?.receivingBankCountry, this.parentForm?.value?.bankPhoneNumber ?? '');
      }
    }

    combineLatest([this.store.select(selectABA), this.store.select(selectABAStandingAuth), this.store.select(selectTaxRemittance)])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([aba, abaStandingAuth, taxRemittance]) => {

        let abaValue = this.isFromCreateTransaction ? aba : abaStandingAuth;
        this.aba?.setValue(abaValue);
        if (taxRemittance?.taxId) {
          createControl(this.parentForm, 'taxPayerId', taxRemittance.taxId, undefined, undefined, undefined, true);
        }
        if (taxRemittance?.taxTypeCode) {
          createControl(this.parentForm, 'taxTypeCode', taxRemittance.taxTypeCode, undefined, undefined, undefined, true);
        }
        if (taxRemittance?.taxMonthCode) {
          createControl(this.parentForm, 'taxMonth', taxRemittance.taxMonthCode, undefined, undefined, undefined, true);
        }
        if (taxRemittance?.taxYear) {
          createControl(this.parentForm, 'taxYear', taxRemittance.taxYear, undefined, undefined, undefined, true);
        }

      })

    this.wireActions$
      .pipe(
        ofType(WireActions.wireDetailsCallSuccessful, StandingAuthWireActions.wireDetailsCallSuccessful),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (
          this.aba?.value !== this.wireInfo?.receivingBankDetails?.recBankAba &&
          this.wireInfo?.receivingBankDetails?.recBankAba !== '' &&
          this.wireInfo?.receivingBankDetails?.recBankAba !== ' ' &&
          this.wireInfo?.receivingBankDetails?.recBankAba !== '000000000'
        )
          this.aba?.setValue(this.wireInfo?.receivingBankDetails?.recBankAba);
      });

      const receivingBankCountry = this.parentForm?.value?.receivingBankCountry?.toUpperCase();
      const isBankCountryUS = receivingBankCountry === 'US' || receivingBankCountry === 'UNITED STATES';

      this.abaLabel = this.useSloa
          ? (isBankCountryUS ? Constants.abaNumber : transactionDetailLabels.intermediaryUseSloaBicBankDetails)
          : Constants.abaNumber;

      this.abaInfoLabel = this.getAbaInfoLabel();
      combineLatest([
        this.store.select(selectTemplatesList),
        this.store.select(selectTemplateId)
       ]).pipe(takeUntil(this.destroy$))
        .subscribe(([templateList, templateId]) => {
          this.templates = templateList;
          this.templateId = templateId;
      });

      let templateInContext = this.templates?.find(template => template?.draftId === this.templateId);
      if (this.aba?.value && this.wireInfo?.receivingBankDetailsIso?.recBankAddress?.countrySubDivision == ''
                          && templateInContext) {
          this.showIsoReceivingBankDetails = false;
          this.store.dispatch(WireActions.callWireDetailsV2({ isPalAccountInContext: this.palAccountInContext }));
      }

   this.wireDetailsError$
      .pipe(takeUntil(this.destroy$))
      .subscribe((error) => {
        this.detailsApiError = error;
        /* This error focusing is needed here because the error comes after the field is blurred and the ABA call is made.
           There is a scenario where, without blurring the aba, the user can scroll and click continue.
           This would cause the ABA call to go out, the transaction-form.components scroll-to-error-text on continue click to fire,
           and then the ABA call finishes with an error, not getting scrolled to.
        */
        if (error) {
          setTimeout(() => {
            const abaEl = document.getElementById('wire-aba-container');
            // -190 to account for top nav bar + cash box
            if (abaEl && ((abaEl.getBoundingClientRect().top - 190) < 0)) {
              abaEl?.focus();
              abaEl?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'start' });
            }
          }, 0)
        }
      });

    this.wireDetailsWarning$
      .pipe(takeUntil(this.destroy$))
      .subscribe((warning) => (this.detailsApiWarning = warning));
  }

  getAbaInfoLabel(): string {
    if(this.isFromIntermediaryInterim)
    {
      if(this.useSloa) {
        return transactionDetailLabels.intermediaryBankInformation; // bank information
      } else {
        return transactionDetailLabels.intermediaryBankDetails // intermediary bank information
      }
    }
    return transactionDetailLabels.intermediaryBankInformation; // bank information
  }

  getIntermediarySloaReadOnlyAddress(name?: string, city?: string, state?: string, country?: string) {
    return this.formatIsoAddressFieldsService.getFieldsValue(name) +
      this.formatIsoAddressFieldsService.getNextLine(name) +
      this.formatIsoAddressFieldsService.getFieldsValue(city, state) +
      this.formatIsoAddressFieldsService.getFieldsValue(state) +
      this.formatIsoAddressFieldsService.getNextLine(city, state) +
      this.formatIsoAddressFieldsService.getFieldsValue(country) +
      this.formatIsoAddressFieldsService.getNextLine(country)
  }

  getSloaReadOnlyNonCompliantAddress(name?: string, nameCount?: string, address?: string, addressCount?: string, country?: string, phone?: string) {
    return this.formatIsoAddressFieldsService.getFieldsValue(name) +
      this.formatIsoAddressFieldsService.getNextLine(name) +
      this.formatIsoAddressFieldsService.getFieldsValue(nameCount) +
      this.formatIsoAddressFieldsService.getNextLine(nameCount) +
      this.formatIsoAddressFieldsService.getFieldsValue(address) +
      this.formatIsoAddressFieldsService.getNextLine(address) +
      this.formatIsoAddressFieldsService.getFieldsValue(addressCount) +
      this.formatIsoAddressFieldsService.getNextLine(addressCount) +
      this.formatIsoAddressFieldsService.getFieldsValue(country) +
      this.formatIsoAddressFieldsService.getNextLine(country) +
      phone
  }


  get aba(): UntypedFormControl {
    return this.parentForm.get(this.controlNames.aba) as UntypedFormControl;
  }

  get abaValidationError() {
    if (this.aba && this.aba.errors) {
      const errorKeys: string[] = Object.keys(this.aba.errors ?? {});
      if (errorKeys.length > 0) {
        if (errorKeys.includes('detailsApiError')) {
          addDetailsApiError(this.detailsApiError, this.isFromIntermediaryInterim);
        }
        if (this.isFromIntermediaryInterim) {
          return IntermediaryAbaErrorMap[errorKeys[0] as keyof typeof IntermediaryAbaErrorMap] ?? '';
        }
        else {
          return abaErrorMap[errorKeys[0] as keyof typeof abaErrorMap] ?? '';
        }
      }
    }
  }

  get abaChangedMessage(): string {
    if (this.detailsApiWarning?.includes('ABA number has been changed')) {
      let message = this.detailsApiWarning.split('.');
      return `The receiving bank ${message[0]}`;
    }
    return '';
  }

  get receivingBankName1(): UntypedFormControl {
    return this.parentForm.get(this.controlNames.name1) as UntypedFormControl;
  }

  get receivingBankName2(): UntypedFormControl {
    return this.parentForm.get(this.controlNames.name2) as UntypedFormControl;
  }

  get receivingBankAddress1(): UntypedFormControl {
    return this.parentForm.get(this.controlNames.address1) as UntypedFormControl;
  }

  get receivingBankAddress2(): UntypedFormControl {
    return this.parentForm.get(this.controlNames.address2) as UntypedFormControl;
  }

  get receivingBankCountry(): UntypedFormControl {
    return this.parentForm.get(this.controlNames.country) as UntypedFormControl;
  }

  get receivingBankPhoneNumber(): UntypedFormControl {
    return this.parentForm.get(this.controlNames.bankPhoneNumber) as UntypedFormControl;
  }

  get receivingBankName(): UntypedFormControl {
    return this.parentForm.get(this.controlNames.receivingBankName) as UntypedFormControl;
  }

  get receivingBankCity(): UntypedFormControl {
    return this.parentForm.get(this.controlNames.isoRecAddress.townName) as UntypedFormControl;
  }

  get receivingBankState(): UntypedFormControl {
    return this.parentForm.get(this.controlNames.isoRecAddress.countrySubDivision) as UntypedFormControl;
  }

  get country(): UntypedFormControl {
    return this.parentForm.get(this.controlNames.isoRecAddress.country) as UntypedFormControl;
  }

  blurInputField(e: Event) {
    let input = e.target as HTMLElement;
    input.blur();
  }

  retryDetailsCall() {
    this.wireInfo$
      .pipe(takeUntil(this.destroy$))
      .subscribe((wireInfo) => {
        if (wireInfo.detailsCallFailed) {
          this.aba.updateValueAndValidity();
        }
      });
  }

  clearAba(): void {
    if (this.isAbaReadonly) {
      return;
    }
    this.parentForm.controls.aba.reset();
    this.parentForm.controls.aba.markAsTouched();
    this.parentForm.controls.aba.markAsPristine();
    this.serviceError = false;
    this.serviceErrorText = '';
  }

  displayValidation(control: AbstractControl) {
    if (control?.dirty) {
      this.abaError = control?.invalid;
    } else {
      this.abaError = false;
    }
  }

  get isAbaReadonly(): boolean {
    return document.getElementById("aba")?.hasAttribute('readOnly') ?? false;
  }

  get useTemplateShowFields(): boolean {
    return this.useTemplate && !this.aba.invalid;
  }

  formatBankPhoneNumber(wireInfo: WireInfo): string {
    return [
      wireInfo.receivingBankDetails.bankPhoneArea,
      wireInfo.receivingBankDetails.bankPhoneNum1,
      wireInfo.receivingBankDetails.bankPhoneNum2,
    ]
      .filter(Boolean)
      .join('-');
  }

  ngOnDestroy(): void {
    this.parentForm?.removeControl(this.controlNames.name1);
    this.parentForm?.removeControl(this.controlNames.name2);
    this.parentForm?.removeControl(this.controlNames.address1);
    this.parentForm?.removeControl(this.controlNames.address2);
    this.parentForm?.removeControl(this.controlNames.country);
    this.parentForm?.removeControl(this.controlNames.bankPhoneNumber);
    this.parentForm?.removeControl(this.controlNames.receivingBankName);
    this.parentForm?.removeControl(this.controlNames.isoRecAddress.townName);
    this.parentForm?.removeControl(this.controlNames.isoRecAddress.countrySubDivision);
    this.parentForm?.removeControl(this.controlNames.isoRecAddress.country);
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  private createControl(
    name: string,
    value: string,
    validators?: ValidatorFn[],
    asyncValidators?: AsyncValidatorFn[],
    updatedOn?: 'change' | 'blur' | 'submit'
  ) {
    if (!this.parentForm.contains(name)) {
      this.parentForm.setControl(
        name,
        this.fb.control(value, {
          validators: validators || [],
          asyncValidators: asyncValidators,
          updateOn: updatedOn || 'change'
        })
      );
    }
    else {
      const control = this.parentForm.get(name);

      if (this.useSloa) {
        control?.updateValueAndValidity();
        return;
      }

      if (!control?.validator && validators) {
        control?.setValidators(validators);
      }
      if (!control?.asyncValidator && asyncValidators) {
        this.abaAsyncValidator.controlPreviousValue = control?.value;
        control?.setAsyncValidators(asyncValidators);
      }
      control?.updateValueAndValidity();
    }
  }

  private getCountryDescription(countryList: Country[], countryInfo: string): string {
    return countryList.find(c => c.code === countryInfo)?.description ?? countryInfo;
  }
}
